import { useEffect, useState } from "react";
import { Download, FileText, Image as ImageIcon, Loader2, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { adminOrderFiles } from "../../lib/api";

/**
 * Files & Mockups gallery — rendered inside the OrderDetailModal.
 * Pulls every retrievable file for the order (mockups + artwork + DTF sheets)
 * and shows them as a thumbnail grid with one-click download.
 */
const KIND_LABEL = {
  mockup_front: "Mockup — Front",
  mockup_back: "Mockup — Back",
  artwork: "Customer Artwork",
  dtf_sheet: "Gang Sheet",
};

const KIND_BADGE = {
  mockup_front: "border-blue-700 text-blue-300",
  mockup_back: "border-blue-700 text-blue-300",
  artwork: "border-[var(--tc-red)] text-[var(--tc-red)]",
  dtf_sheet: "border-purple-700 text-purple-300",
};

const friendlyBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function OrderFilesGallery({ orderId, pw }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || data || !orderId) return;
    setLoading(true);
    adminOrderFiles(orderId, pw)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load files"))
      .finally(() => setLoading(false));
  }, [open, data, orderId, pw]);

  const downloadAll = () => {
    if (!data?.files?.length) return;
    // Trigger a brief stagger so the browser doesn't queue-merge them.
    data.files.forEach((f, i) => {
      setTimeout(() => downloadDataUrl(f.data_url, f.name), i * 150);
    });
  };

  return (
    <section className="surface-card p-4" data-testid="order-files-gallery">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="order-files-toggle"
        className="w-full flex items-center justify-between text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-[var(--tc-red)]" />
          <span className="font-display text-lg uppercase">Files & Mockups</span>
          {data && (
            <span className="font-mono text-[10px] text-zinc-500 ml-2">
              {data.counts.mockups}M · {data.counts.artwork}A
              {data.counts.dtf_sheets > 0 ? ` · ${data.counts.dtf_sheets}DTF` : ""}
              {data.archived ? " · archived" : " · live"}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <>
          {loading && (
            <div className="py-8 text-center text-zinc-500 font-mono text-xs flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading files…
            </div>
          )}

          {!loading && data && data.files.length === 0 && (
            <div className="py-8 text-center" data-testid="order-files-empty">
              <ImageIcon size={28} className="mx-auto text-zinc-700 mb-3" />
              <div className="font-mono text-xs text-zinc-500">
                No archived files for this order.
                <br />
                {data.archived
                  ? "The order was processed but no mockups/artwork were stored."
                  : "Live blobs haven't been archived yet (this usually means the order is brand new)."}
              </div>
            </div>
          )}

          {!loading && data && data.files.length > 0 && (
            <>
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={downloadAll}
                  data-testid="order-files-download-all"
                  className="font-mono text-[10px] tracking-widest border border-[#222] hover:border-white px-3 py-1.5 inline-flex items-center gap-2"
                >
                  <Download size={11} /> DOWNLOAD ALL ({data.files.length})
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="order-files-grid">
                {data.files.map((f, idx) => (
                  <div
                    key={idx}
                    className="border border-[#222] bg-zinc-950 group hover:border-[var(--tc-red)] transition-colors"
                    data-testid={`order-file-${f.kind}-${idx}`}
                  >
                    <button
                      type="button"
                      onClick={() => setPreview(f)}
                      className="block w-full aspect-square bg-white relative overflow-hidden"
                      title="Click to preview"
                    >
                      {f.mime.startsWith("image/") ? (
                        <img
                          src={f.data_url}
                          alt={f.name}
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 bg-zinc-100">
                          <FileText size={28} />
                          <span className="font-mono text-[9px] mt-1">{f.mime.split("/").pop()?.toUpperCase()}</span>
                        </div>
                      )}
                      <span className={`absolute top-1 left-1 chip text-[8px] px-1.5 py-0.5 ${KIND_BADGE[f.kind] || "border-zinc-700 text-zinc-400"}`}>
                        {KIND_LABEL[f.kind] || f.kind}
                      </span>
                    </button>
                    <div className="p-2 flex justify-between items-center gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-zinc-300 truncate" title={f.name}>{f.name}</div>
                        <div className="font-mono text-[9px] text-zinc-600">
                          {f.item_label} {f.size_bytes ? `· ${friendlyBytes(f.size_bytes)}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setPreview(f)}
                          data-testid={`order-file-preview-${idx}`}
                          className="border border-[#222] hover:border-white text-zinc-300 p-1.5"
                          title="Preview"
                        >
                          <Eye size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDataUrl(f.data_url, f.name)}
                          data-testid={`order-file-download-${idx}`}
                          className="border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] text-zinc-300 p-1.5"
                          title="Download"
                        >
                          <Download size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Lightbox preview */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="order-file-lightbox"
        >
          <div className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
            {preview.mime.startsWith("image/") ? (
              <img src={preview.data_url} alt={preview.name} className="max-w-full max-h-[80vh] object-contain bg-white" />
            ) : (
              <iframe src={preview.data_url} title={preview.name} className="w-[90vw] h-[80vh] bg-white" />
            )}
            <div className="mt-3 flex justify-between items-center text-zinc-300 gap-3">
              <div className="font-mono text-xs truncate">{preview.name}</div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => downloadDataUrl(preview.data_url, preview.name)}
                  className="font-mono text-[10px] tracking-widest border border-[#222] hover:border-white px-3 py-1.5 inline-flex items-center gap-2"
                >
                  <Download size={11} /> DOWNLOAD
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="font-mono text-[10px] tracking-widest border border-[#222] hover:border-white px-3 py-1.5"
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
